import { useState, useEffect, useCallback, useRef } from "react";
import api from "../services/api";

/**
 * Hook para GET requests com estado loading/error/data e cancel automático.
 *
 * @param {string|null} url - Endpoint a buscar (null = não busca)
 * @param {object} options
 * @param {Array} options.deps - Dependências extras para re-fetch (default: [])
 * @param {Function} [options.transform] - Função para transformar o dado. Deve ser memoizada
 *   com useCallback ou definida fora do componente para evitar re-fetches desnecessários.
 * @param {boolean} options.immediate - Se deve buscar imediatamente ao montar (default: true)
 * @returns {{ data: any, loading: boolean, error: string|null, refetch: Function }}
 */
export function useFetch(url, { deps = [], transform, immediate = true } = {}) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(immediate && url != null);
  const [error, setError] = useState(null);
  const abortRef = useRef(null);

  const fetch = useCallback(async () => {
    if (!url) return;

    if (abortRef.current) abortRef.current.abort();
    const controller = new AbortController();
    abortRef.current = controller;

    setLoading(true);
    setError(null);

    try {
      const response = await api.get(url, { signal: controller.signal });
      const result = transform ? transform(response.data) : response.data;
      setData(result);
    } catch (err) {
      if (err.name === "CanceledError" || err.name === "AbortError") return;
      setError(err.response?.data?.error ?? err.message ?? "Erro ao carregar dados");
    } finally {
      setLoading(false);
    }
  }, [url, transform, ...deps]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (immediate) fetch();
    return () => abortRef.current?.abort();
  }, [fetch, immediate]);

  return { data, loading, error, refetch: fetch };
}
